import React, { useEffect, useRef, useState } from 'react';
import {
	Platform,
	StyleSheet,
	Linking,
	Animated,
	TouchableOpacity,
	Image,
} from 'react-native';
import { FAB } from 'react-native-paper';
import { useSelector, useDispatch } from 'react-redux';
import icons from '../../../config/icons';
import useDevicePlatform, {
	DeviceType,
} from '../../../hooks/useDevicePlatform.hook';
import { setChatCollapsed } from '../../../store/actions/config.actions';
import {
	getChatCollapsed,
	getHideChat,
} from '../../../store/selectors/config.selectors';
import {
	getIsInWorkerCard,
	isLoggedIn,
} from '../../../store/selectors/login.selectors';
import Chat from './Chat';

const ChatWrapper = () => {
	const loggedIn = useSelector(isLoggedIn);

	const [openChat, setOpenChat] = useState(false);
	const isInWorkerCard = useSelector(getIsInWorkerCard);
	const hideChat = useSelector(getHideChat);
	const collapsed = useSelector(getChatCollapsed);
	const dispatch = useDispatch();
	const platform = useDevicePlatform();

	// Collapse-to-handle + left position apply to the native mobile app only.
	const isMobile = Platform.OS !== 'web';

	// 0 = FAB shown, 1 = handle shown. Cross-fades + slides between the two.
	const anim = useRef(new Animated.Value(0)).current;
	const fabOpacity = anim.interpolate({
		inputRange: [0, 1],
		outputRange: [1, 0],
	});
	const fabTranslate = anim.interpolate({
		inputRange: [0, 1],
		outputRange: [0, -40],
	});
	const handleTranslate = anim.interpolate({
		inputRange: [0, 1],
		outputRange: [-40, 0],
	});

	useEffect(() => {
		setOpenChat(false);
	}, [loggedIn]);

	useEffect(() => {
		Animated.timing(anim, {
			toValue: collapsed ? 1 : 0,
			duration: 200,
			useNativeDriver: true,
		}).start();
	}, [collapsed, anim]);

	// Re-syncs position on reveal to fix an iOS remount bug that freezes or hides the FAB.
	// Only triggers on visibility changes—collapse/expand state is handled by the main animation.
	useEffect(() => {
		if (!isInWorkerCard && !hideChat) {
			anim.setValue(collapsed ? 1 : 0);
		}
	}, [isInWorkerCard, hideChat]);

	const onPressOpen = () => {
		Platform.OS === 'web'
			? setOpenChat(!openChat)
			: Linking.openURL('https://wa.me/97236791615');
	};

	return (
		<>
			{openChat ? <Chat /> : null}
			{!isInWorkerCard && !hideChat ? (
				isMobile ? (
					<>
						{/* Expanded: WhatsApp FAB + close (collapse) badge */}
						<Animated.View
							pointerEvents={collapsed ? 'none' : 'box-none'}
							style={[
								styles.mobileContainer,
								{
									opacity: fabOpacity,
									transform: [{ translateX: fabTranslate }],
								},
							]}
						>
							<FAB
								style={styles.fab}
								small
								icon={icons.whatsappIcon}
								onPress={onPressOpen}
							/>
							<TouchableOpacity
								style={styles.closeBadge}
								onPress={() => dispatch(setChatCollapsed(true))}
								hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
							>
								<Image
									source={icons.closeBig}
									style={styles.closeIcon}
								/>
							</TouchableOpacity>
						</Animated.View>

						{/* Collapsed: handle docked to the left edge */}
						<Animated.View
							pointerEvents={collapsed ? 'box-none' : 'none'}
							style={[
								styles.handle,
								{
									opacity: anim,
									transform: [{ translateX: handleTranslate }],
								},
							]}
						>
							<TouchableOpacity
								style={styles.handleTouch}
								onPress={() => dispatch(setChatCollapsed(false))}
							>
								<Image
									source={icons.whatsappIcon}
									style={styles.handleIcon}
								/>
							</TouchableOpacity>
						</Animated.View>
					</>
				) : (
					<FAB
						style={StyleSheet.flatten([
							styles.fab,
							styles.fabWeb,
							{ bottom: platform === DeviceType.WEB ? 50 : 25 },
						])}
						small
						icon={openChat ? icons.closeBig : icons.message}
						onPress={onPressOpen}
					/>
				)
			) : null}
		</>
	);
};

const styles = StyleSheet.create({
	fab: {
		padding: 15,
		borderRadius: 35,
		backgroundColor: '#1B80C4',
		width: 70,
		height: 70,
		zIndex: 100,
	},
	fabWeb: {
		position: 'absolute',
		right: 20,
	},
	mobileContainer: {
		position: 'absolute',
		left: 20,
		bottom: 25,
		width: 70,
		height: 70,
		zIndex: 100,
	},
	closeBadge: {
		position: 'absolute',
		top: -6,
		right: -6,
		width: 24,
		height: 24,
		borderRadius: 12,
		backgroundColor: '#4A4A4A',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 100,
		elevation: 5,
	},
	closeIcon: {
		width: 12,
		height: 12,
		resizeMode: 'contain',
	},
	handle: {
		position: 'absolute',
		left: 0,
		bottom: 34,
		zIndex: 100,
	},
	handleTouch: {
		width: 26,
		height: 52,
		backgroundColor: '#1B80C4',
		borderTopRightRadius: 26,
		borderBottomRightRadius: 26,
		alignItems: 'center',
		justifyContent: 'center',
		paddingLeft: 2,
		elevation: 5,
	},
	handleIcon: {
		width: 18,
		height: 18,
		resizeMode: 'contain',
	},
});

export default ChatWrapper;
